import React, { useState, useEffect } from 'react';
import { styles as globalStyles } from '../../../utils/styles';
import { useEditor } from '../../../contexts/EditorContext';
import { Container } from '../../../types/canvas.types';
import { DownArrow, UpArrow } from 'assests/GmsIcons';
import { ConfirmationDialog } from '../../Common/ConfirmationDialog';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import {
  isContainerNested as isContainerNestedUtil,
  convertContainerToConfig as convertContainerToConfigUtil
} from './utils/containerHelpers';
import { findContainerById as findContainerByIdUtil } from '../../../utils/containerSearch';
import { useContainerWidth } from './hooks/useContainerWidth';
import { useContainerDelete } from './hooks/useContainerDelete';
import { useContainerDragDrop } from './hooks/useContainerDragDrop';
import { ContainerConfigPanel } from './components/ContainerConfigPanel';
import { ContainerContentsList } from './components/ContainerContentsList';

type ContainerConfigType = {
  id: string;
  name: string;
  width: number;
  widthUnit: '%' | 'px';
  height: 'Dynamic' | number;
  heightUnit: 'px';
  alignment: 'left' | 'center' | 'right';
  layout: 'vertical' | 'horizontal';
  showTitle: boolean;
  widthLocked?: boolean;
  collapsible?: boolean;
  behaveAsFilter?: boolean;
}

type ContainerConfigProps = {
  id?: string;
  containerId?: string;
}

export const ContainerConfig = ({ id, containerId }: ContainerConfigProps) => {
  const baseId = id || 'container-config';
  const { editorState, containers, setContainers, setEditorState, removeComponentFromContainer } = useEditor();
  const {  releaseComponent, removeContainerFromHierarchy, getComponentDefinition, getAllComponentsWithUsageInfo} = useComponentRegistry();
  const [isContentsExpanded, setIsContentsExpanded] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);

  const selectedContainerId = containerId || editorState.selectedContainerId;
  const activePageId = editorState.activePageId;

  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);

  const defaultConfig: ContainerConfigType = {
    id: 'container-1',
    name: 'Container 1',
    width: 80,
    widthUnit: '%',
    height: 'Dynamic',
    heightUnit: 'px',
    alignment: 'left',
    layout: 'vertical',
    showTitle: false,
    widthLocked: false,
    collapsible: false,
    behaveAsFilter: false
  };

  const [config, setConfig] = useState<ContainerConfigType>(defaultConfig);

  const isContainerNested = (containerId: string): boolean => {
    return isContainerNestedUtil(containerId, activePageId, containers);
  };

  const {
    constraintMessage,
    showConstraintMessage,
    handleWidthChange,
    resetRowWidths,
    toggleWidthLock,
    getContainersInSameRow
  } = useContainerWidth(
    selectedContainerId,
    activePageId,
    containers,
    selectedContainer,
    isContainerNested,
    setConfig,
    setContainers
  );

  const {
    deleteConfirmation,
    isDeleting,
    handleDeleteComponent,
    handleDeleteNestedContainer,
    confirmDelete,
    cancelDelete
  } = useContainerDelete(
    selectedContainerId,
    selectedContainer,
    activePageId,
    containers,
    releaseComponent,
    removeComponentFromContainer,
    getComponentDefinition,
    getAllComponentsWithUsageInfo,
    removeContainerFromHierarchy,
    setContainers
  );

  const {
    handleOnDragEnd,
    handleNestedItemDragEnd
  } = useContainerDragDrop(
    selectedContainerId,
    selectedContainer,
    activePageId,
    setContainers
  );

  const findContainerById = findContainerByIdUtil;
  const convertContainerToConfig = convertContainerToConfigUtil;

  useEffect(() => {
    if (selectedContainerId && activePageId && containers[activePageId]) {
      const container = findContainerById(containers[activePageId], selectedContainerId);

      if (container) {
        setSelectedContainer(container);

        const containerConfig = convertContainerToConfig(container);
        setConfig(containerConfig);
      } else {
        console.warn("Container not found in ContainerConfig:", selectedContainerId);
      }
    }
  }, [selectedContainerId, activePageId, containers, findContainerById, convertContainerToConfig]);

  const handleWidthInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      handleWidthChange(value);
    }
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };


  const handleItemClick = (item: any, containerId: string) => {
    if (item.type === 'field' || item.fieldType) {
      setEditorState(prev => ({
        ...prev,
        selectedFieldId: item.id,
        selectedFieldType: item.fieldType || item.type,
        selectedFieldParentId: item.parentId,
        selectedContainerId: containerId,
        selectedSubmodelId: null
      }));
    } else if (item.type === 'widget') {
      setEditorState(prev => ({
        ...prev,
        selectedContainerId: containerId,
        selectedFieldId: null,
        selectedFieldType: null,
        selectedFieldParentId: null,
        selectedSubmodelId: null
      }));
    }
  };

  const handleNestedContainerClick = (nestedContainerId: string) => {
    setEditorState(prev => ({
      ...prev,
      selectedContainerId: nestedContainerId,
      selectedFieldId: null,
      selectedFieldType: null,
      selectedFieldParentId: null,
      selectedSubmodelId: null
    }));
  };

  const handleSubmodelClick = (submodelId: string, submodelName: string) => {
    setEditorState(prev => ({
      ...prev,
      selectedSubmodelId: submodelId,
      selectedSubmodelName: submodelName,
      selectedFieldId: null,
      selectedFieldType: null,
      selectedFieldParentId: null
    }));
  };


  const containersInRow = getContainersInSameRow();
  const containersCount = containersInRow.length;

  

  return (
    <div id={baseId} data-testid={baseId} className={styles.container}>
      {/* Container Header */}
      <div id={`${baseId}-header`} className={styles.header}>
        <h2 id={`${baseId}-header-title`} className={styles.headerTitle}>{config.name}</h2>
        <button
          id={`${baseId}-collapse-btn`}
          data-testid={`${baseId}-collapse-btn`}
          onClick={toggleExpand}
          className={styles.collapseButton}
        >
          {isExpanded ? <UpArrow /> : <DownArrow />}
        </button>
      </div>

      {isExpanded && (
        <>
          {/* Width Constraint Message */}
          {showConstraintMessage && (
            <div id={`${baseId}-constraint-message`} className="tw-bg-yellow-100 tw-text-yellow-800 tw-p-2 tw-mb-3 tw-rounded-md tw-text-sm">
              {constraintMessage}
            </div>
          )}

          {/* Container Configuration Panel */}
          <ContainerConfigPanel
            id={`${baseId}-config-panel`}
            config={config}
            setConfig={setConfig}
            selectedContainerId={selectedContainerId}
            activePageId={activePageId}
            containers={containers}
            setContainers={setContainers}
            containersCount={containersCount}
            containersInRow={containersInRow}
            handleWidthInputChange={handleWidthInputChange}
            handleWidthChange={handleWidthChange}
            toggleWidthLock={toggleWidthLock}
            resetRowWidths={resetRowWidths}
            isContainerNested={isContainerNested}
          />

          {/* Container Contents List */}
          <ContainerContentsList
            id={`${baseId}-contents-list`}
            selectedContainer={selectedContainer}
            isContentsExpanded={isContentsExpanded}
            setIsContentsExpanded={setIsContentsExpanded}
            handleItemClick={handleItemClick}
            handleNestedContainerClick={handleNestedContainerClick}
            handleSubmodelClick={handleSubmodelClick}
            handleDeleteComponent={handleDeleteComponent}
            handleDeleteNestedContainer={handleDeleteNestedContainer}
            handleOnDragEnd={handleOnDragEnd}
            handleNestedItemDragEnd={handleNestedItemDragEnd}
          />
        </>
      )}
      <ConfirmationDialog
        id={`${baseId}-delete-confirmation`}
        isOpen={deleteConfirmation.isOpen}
        title={`Delete ${deleteConfirmation.type === 'component' ? 'Component' : 'Container'}`}
        message={`Are you sure you want to delete "${deleteConfirmation.name}"? ${
            deleteConfirmation.type === 'nested-container'
                ? 'All components within this container will also be deleted.'
                : ''
        } This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

const styles = {
  container: "",
  header: `${globalStyles.sectionTitle} tw-flex tw-justify-between tw-items-center`,
  headerTitle: "tw-text-[#6D788C] tw-font-semibold",
  collapseButton: "tw-rounded tw-bg-gainsboro-100 tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center",
  section: "tw-mb-4 tw-mx-4",
  sectionLabel: "tw-text-[#6D788C] tw-text-sm tw-mb-2",
  widthContainer: "tw-flex tw-items-center tw-gap-4",
  valueInput: "tw-flex tw-items-center tw-border tw-text-[#091E42] tw-border-[#97A0AF] tw-rounded tw-px-2 tw-py-1 tw-min-w-[70px]",
  numericInput: "tw-w-9 tw-outline-none",
  dynamicHeightText: "tw-text-[#091E42] tw-text-sm tw-font-semibold",
  layoutDropdown: "tw-relative",
  select: "tw-w-full tw-appearance-none tw-border tw-border-[#DADBDD] tw-rounded tw-px-3 tw-py-2 tw-pr-8 tw-text-[#172B4D] tw-text-sm tw-bg-white",
  selectIcon: "tw-absolute tw-right-3 tw-top-1/2 tw-transform tw-translate-y-[-50%]",
  checkboxContainer: "tw-flex tw-items-center",
  checkboxInput: `${globalStyles.checkboxInput}`,
  checkboxLabel: "tw-text-[#172B4D] tw-text-sm",
};
